import React, { useState, useEffect } from 'react'
import AppLayout from '../components/AppLayout'
import { useAuth } from '../contexts/AuthContext'
import { supabase, ugandaDate, ugandaMonth, ugandaDatetime } from '../lib/supabase'

const DEPARTMENTS = ['Farm Operations','Harvest','Logistics','Packaging','Finance','Management','General']
const CONTRACT_TYPES = ['Full-time','Part-time','Contract','Casual / Daily','Seasonal']
const SALARY_FREQ = ['Monthly','Weekly','Daily','Per Task']
const RANKS = ['Director','Manager','Supervisor','Senior Staff','Staff','Junior Staff','Casual Worker']

export default function StaffRegister() {
  const { user, isAdmin, isAdminOrManager } = useAuth()
  const [tab, setTab] = useState('list')
  const [staff, setStaff] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)
  const [selected, setSelected] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [uploadingDoc, setUploadingDoc] = useState(false)
  const [provisioningAll, setProvisioningAll] = useState(false)

  const emptyForm = () => ({
    full_name: '', employee_id: '', rank: '', department: '',
    contract_type: 'Full-time', job_title: '', start_date: '',
    end_date: '', phone: '', email: '', national_id: '',
    emergency_contact_name: '', emergency_contact_phone: '',
    salary_amount: '', salary_frequency: 'Monthly', salary_currency: 'UGX',
    bank_name: '', bank_account: '', mobile_money: '',
    notes: '', is_active: true,
    // CV / Documents
    cv_url: '', contract_url: '', id_scan_url: '',
  })

  const [form, setForm] = useState(emptyForm())

  useEffect(() => { loadStaff() }, [])

  const loadStaff = async () => {
    setLoading(true)
    const { data } = await supabase.from('staff_register').select('*').order('full_name')
    setStaff(data || [])
    setLoading(false)
  }

  const handleEdit = (s) => {
    setSelected(s)
    setForm({ ...emptyForm(), ...s })
    setTab('form')
    setShowForm(true)
  }

  const handleNew = () => {
    setSelected(null)
    setForm(emptyForm())
    setTab('form')
    setShowForm(true)
  }

  const handleSave = async () => {
    if (!form.full_name) { setMsg({ type:'error', text:'Full name is required.' }); return }
    setSaving(true); setMsg(null)
    const payload = {
      ...form,
      salary_amount: parseFloat(form.salary_amount) || null,
      updated_by: user.id,
    }
    let error
    if (selected) {
      ({ error } = await supabase.from('staff_register').update(payload).eq('id', selected.id))
    } else {
      payload.created_by = user.id
      payload.employee_id = payload.employee_id || `EMP-${Date.now().toString().slice(-5)}`
      ;({ error } = await supabase.from('staff_register').insert(payload))
    }
    if (error) { setMsg({ type:'error', text:error.message }); setSaving(false); return }
    setMsg({ type:'success', text: selected ? 'Staff record updated.' : 'Staff member added.' })
    setSaving(false); setTab('list'); setShowForm(false); loadStaff()
    setTimeout(() => setMsg(null), 3000)
  }

  const handleDocUpload = async (field, file) => {
    if (!file) return
    setUploadingDoc(true)
    const ext = file.name.split('.').pop()
    const path = `staff-docs/${form.full_name.replace(/\s+/g,'-')}_${field}_${Date.now()}.${ext}`
    const { error: uploadErr } = await supabase.storage.from('staff-docs').upload(path, file)
    if (uploadErr) { setMsg({ type:'error', text:'Upload failed: '+uploadErr.message }); setUploadingDoc(false); return }
    const { data: { publicUrl } } = supabase.storage.from('staff-docs').getPublicUrl(path)
    setForm(f => ({ ...f, [field]: publicUrl }))
    setUploadingDoc(false)
  }

  // Auto-provision salaries for all active staff for current month
  const handleProvisionSalaries = async () => {
    const activeStaff = staff.filter(s => s.is_active && s.salary_amount && s.salary_frequency === 'Monthly')
    if (!activeStaff.length) { setMsg({ type:'error', text:'No active staff with monthly salaries found.' }); return }

    const month = ugandaMonth()
    setProvisioningAll(true)

    // Check which ones already have provisions this month
    const { data: existing } = await supabase.from('accrual_provisions')
      .select('description').eq('period_month', month).eq('category', 'Wages')

    const existingNames = (existing || []).map(e => e.description)
    const toProvision = activeStaff.filter(s => !existingNames.includes(s.full_name))

    if (!toProvision.length) {
      setMsg({ type:'success', text:'Salary provisions for this month already exist for all staff.' })
      setProvisioningAll(false); return
    }

    const inserts = toProvision.map(s => ({
      user_id: user.id,
      period_month: month,
      category: 'Wages',
      description: s.full_name,
      estimated_amount: parseFloat(s.salary_amount),
    }))

    const { error } = await supabase.from('accrual_provisions').insert(inserts)
    if (error) { setMsg({ type:'error', text:error.message }); setProvisioningAll(false); return }

    const totalProvisioned = toProvision.reduce((s, e) => s + parseFloat(e.salary_amount), 0)
    setMsg({ type:'success', text:`✅ Salary provisions created for ${toProvision.length} staff members. Total: UGX ${totalProvisioned.toLocaleString()}. They now appear in your Weekly P&L accruals.` })
    setProvisioningAll(false)
    setTimeout(() => setMsg(null), 6000)
  }

  const activeCount = staff.filter(s => s.is_active).length
  const monthlyPayroll = staff.filter(s => s.is_active && s.salary_frequency === 'Monthly')
    .reduce((s, e) => s + (parseFloat(e.salary_amount) || 0), 0)

  const rankColor = {
    'Director': 'badge-red', 'Manager': 'badge-green', 'Supervisor': 'badge-blue',
    'Senior Staff': 'badge-amber', 'Staff': 'badge-grey', 'Junior Staff': 'badge-grey', 'Casual Worker': 'badge-grey'
  }

  return (
    <AppLayout title="Staff Register">
      <div className="section-header">
        <div className="section-icon">👥</div>
        <div className="section-info">
          <h2>Staff Register</h2>
          <p>Employee records, salaries, contracts, and documents. Salary provisions auto-created each month.</p>
        </div>
        {isAdminOrManager && (
          <div style={{ display:'flex', gap:8, marginLeft:'auto', flexWrap:'wrap' }}>
            <button className="btn btn-secondary" onClick={handleProvisionSalaries} disabled={provisioningAll}>
              {provisioningAll ? <><span className="spinner"/>Provisioning...</> : '📌 Provision Salaries'}
            </button>
            <button className="btn btn-primary" onClick={handleNew}>+ Add Staff</button>
          </div>
        )}
      </div>

      {msg && <div className={`alert ${msg.type==='success'?'alert-green':'alert-red'}`}>{msg.text}</div>}

      {/* KPI summary */}
      <div className="kpi-grid" style={{ marginBottom:18 }}>
        <div className="kpi"><div className="kpi-label">Active Staff</div><div className="kpi-value">{activeCount}</div></div>
        <div className="kpi"><div className="kpi-label">Monthly Payroll</div><div className="kpi-value" style={{fontSize:'1rem'}}>UGX {monthlyPayroll.toLocaleString()}</div></div>
        <div className="kpi"><div className="kpi-label">Total Headcount</div><div className="kpi-value">{staff.length}</div></div>
        <div className="kpi"><div className="kpi-label">Departments</div><div className="kpi-value">{[...new Set(staff.map(s=>s.department).filter(Boolean))].length}</div></div>
      </div>

      <div className="tabs">
        <button className={`tab ${tab==='list'?'active':''}`} onClick={()=>setTab('list')}>👥 All Staff</button>
        {showForm && <button className={`tab ${tab==='form'?'active':''}`} onClick={()=>setTab('form')}>{selected?'✏️ Edit':'+ New'} Staff</button>}
      </div>

      {/* STAFF LIST */}
      {tab==='list' && (
        <div className="card">
          {loading ? <div style={{textAlign:'center',padding:32}}><span className="spinner dark"/></div>
          : staff.length===0 ? (
            <div className="empty-state">
              <div className="empty-icon">👥</div>
              <h3>No staff records yet</h3>
              <p>Add your first staff member to get started. You can also import manual workers added in Admin Users.</p>
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Employee ID</th><th>Name</th><th>Rank</th><th>Department</th>
                    <th>Contract</th><th>Salary (UGX)</th><th>Frequency</th>
                    <th>Start Date</th><th>Status</th><th>Documents</th>
                    {isAdminOrManager && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {staff.map(s => (
                    <tr key={s.id} style={{ opacity: s.is_active ? 1 : 0.5 }}>
                      <td style={{ fontFamily:"'DM Mono',monospace", fontSize:'0.75rem', color:'var(--text-muted)' }}>{s.employee_id||'—'}</td>
                      <td>
                        <div style={{ fontWeight:700 }}>{s.full_name}</div>
                        <div style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>{s.job_title||''}</div>
                      </td>
                      <td><span className={`badge ${rankColor[s.rank]||'badge-grey'}`}>{s.rank||'—'}</span></td>
                      <td style={{ fontSize:'0.78rem' }}>{s.department||'—'}</td>
                      <td style={{ fontSize:'0.78rem' }}>{s.contract_type||'—'}</td>
                      <td style={{ fontFamily:"'DM Mono',monospace", fontWeight:600 }}>
                        {s.salary_amount ? parseFloat(s.salary_amount).toLocaleString() : '—'}
                      </td>
                      <td style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>{s.salary_frequency||'—'}</td>
                      <td style={{ fontSize:'0.75rem' }}>{s.start_date ? new Date(s.start_date).toLocaleDateString('en-UG') : '—'}</td>
                      <td><span className={`badge ${s.is_active?'badge-green':'badge-grey'}`}>{s.is_active?'Active':'Inactive'}</span></td>
                      <td>
                        <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                          {s.cv_url && <a href={s.cv_url} target="_blank" rel="noreferrer" style={{ fontSize:'0.72rem', color:'var(--blue)' }}>📄 CV</a>}
                          {s.contract_url && <a href={s.contract_url} target="_blank" rel="noreferrer" style={{ fontSize:'0.72rem', color:'var(--blue)' }}>📝 Contract</a>}
                          {s.id_scan_url && <a href={s.id_scan_url} target="_blank" rel="noreferrer" style={{ fontSize:'0.72rem', color:'var(--blue)' }}>🪪 ID</a>}
                          {!s.cv_url && !s.contract_url && !s.id_scan_url && <span style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>None</span>}
                        </div>
                      </td>
                      {isAdminOrManager && (
                        <td>
                          <button className="btn btn-secondary btn-sm" onClick={() => handleEdit(s)}>✏️ Edit</button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Payroll summary by department */}
          {staff.length > 0 && (
            <div style={{ marginTop:24 }}>
              <div style={{ fontWeight:700, fontSize:'0.85rem', marginBottom:12, color:'var(--green-deep)' }}>Payroll by Department</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:10 }}>
                {(() => {
                  const byDept = {}
                  staff.filter(s=>s.is_active).forEach(s => {
                    const dept = s.department || 'Unassigned'
                    if (!byDept[dept]) byDept[dept] = { count:0, total:0 }
                    byDept[dept].count++
                    byDept[dept].total += parseFloat(s.salary_amount)||0
                  })
                  return Object.entries(byDept).map(([dept, { count, total }]) => (
                    <div key={dept} style={{ background:'var(--cream)', borderRadius:8, padding:'10px 14px' }}>
                      <div style={{ fontWeight:700, fontSize:'0.82rem' }}>{dept}</div>
                      <div style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>{count} staff</div>
                      {total > 0 && <div style={{ fontWeight:700, color:'var(--green-deep)', fontFamily:"'DM Mono',monospace", fontSize:'0.85rem', marginTop:4 }}>UGX {total.toLocaleString()}/mo</div>}
                    </div>
                  ))
                })()}
              </div>
            </div>
          )}
        </div>
      )}

      {/* STAFF FORM */}
      {tab==='form' && (
        <div className="card">
          <div className="card-title">{selected ? `Edit — ${selected.full_name}` : 'New Staff Member'}</div>

          {/* Personal Details */}
          <div style={{ fontWeight:700, fontSize:'0.85rem', color:'var(--green-deep)', marginBottom:10, marginTop:4 }}>👤 Personal Details</div>
          <div className="grid-2" style={{ marginBottom:14 }}>
            <div className="form-group"><label className="form-label">Full Name *</label><input className="form-input" value={form.full_name} onChange={e=>setForm(f=>({...f,full_name:e.target.value}))} placeholder="e.g. Sarah Nakato" /></div>
            <div className="form-group"><label className="form-label">Employee ID</label><input className="form-input" value={form.employee_id} onChange={e=>setForm(f=>({...f,employee_id:e.target.value}))} placeholder="Auto-generated if blank" /></div>
            <div className="form-group"><label className="form-label">Phone</label><input className="form-input" value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))} placeholder="+256..." /></div>
            <div className="form-group"><label className="form-label">Email</label><input className="form-input" type="email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} placeholder="Optional" /></div>
            <div className="form-group"><label className="form-label">National ID Number</label><input className="form-input" value={form.national_id} onChange={e=>setForm(f=>({...f,national_id:e.target.value}))} placeholder="CM..." /></div>
          </div>

          {/* Emergency Contact */}
          <div style={{ fontWeight:700, fontSize:'0.85rem', color:'var(--green-deep)', marginBottom:10 }}>🚨 Emergency Contact</div>
          <div className="grid-2" style={{ marginBottom:14 }}>
            <div className="form-group"><label className="form-label">Emergency Contact Name</label><input className="form-input" value={form.emergency_contact_name} onChange={e=>setForm(f=>({...f,emergency_contact_name:e.target.value}))} /></div>
            <div className="form-group"><label className="form-label">Emergency Contact Phone</label><input className="form-input" value={form.emergency_contact_phone} onChange={e=>setForm(f=>({...f,emergency_contact_phone:e.target.value}))} /></div>
          </div>

          {/* Role & Employment */}
          <div style={{ fontWeight:700, fontSize:'0.85rem', color:'var(--green-deep)', marginBottom:10 }}>💼 Role & Employment</div>
          <div className="grid-2" style={{ marginBottom:14 }}>
            <div className="form-group"><label className="form-label">Job Title</label><input className="form-input" value={form.job_title} onChange={e=>setForm(f=>({...f,job_title:e.target.value}))} placeholder="e.g. Farm Supervisor" /></div>
            <div className="form-group"><label className="form-label">Rank</label>
              <select className="form-select" value={form.rank} onChange={e=>setForm(f=>({...f,rank:e.target.value}))}>
                <option value="">Select rank...</option>
                {RANKS.map(r=><option key={r}>{r}</option>)}
              </select>
            </div>
            <div className="form-group"><label className="form-label">Department</label>
              <select className="form-select" value={form.department} onChange={e=>setForm(f=>({...f,department:e.target.value}))}>
                <option value="">Select department...</option>
                {DEPARTMENTS.map(d=><option key={d}>{d}</option>)}
              </select>
            </div>
            <div className="form-group"><label className="form-label">Contract Type</label>
              <select className="form-select" value={form.contract_type} onChange={e=>setForm(f=>({...f,contract_type:e.target.value}))}>
                {CONTRACT_TYPES.map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group"><label className="form-label">Start Date</label><input className="form-input" type="date" value={form.start_date} onChange={e=>setForm(f=>({...f,start_date:e.target.value}))} /></div>
            <div className="form-group"><label className="form-label">End Date (if contract)</label><input className="form-input" type="date" value={form.end_date} onChange={e=>setForm(f=>({...f,end_date:e.target.value}))} /></div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-select" value={form.is_active?'active':'inactive'} onChange={e=>setForm(f=>({...f,is_active:e.target.value==='active'}))}>
                <option value="active">Active</option>
                <option value="inactive">Inactive / Left</option>
              </select>
            </div>
          </div>

          {/* Salary */}
          <div style={{ fontWeight:700, fontSize:'0.85rem', color:'var(--green-deep)', marginBottom:10 }}>💰 Salary & Payment</div>
          <div className="alert alert-amber" style={{ marginBottom:12, fontSize:'0.78rem', padding:'8px 12px' }}>
            💡 Monthly salaries will be auto-provisioned in the P&L each month when you click "Provision Salaries" on the staff list.
          </div>
          <div className="grid-2" style={{ marginBottom:14 }}>
            <div className="form-group"><label className="form-label">Salary Amount (UGX)</label><input className="form-input" type="number" value={form.salary_amount} onChange={e=>setForm(f=>({...f,salary_amount:e.target.value}))} placeholder="0" /></div>
            <div className="form-group"><label className="form-label">Pay Frequency</label>
              <select className="form-select" value={form.salary_frequency} onChange={e=>setForm(f=>({...f,salary_frequency:e.target.value}))}>
                {SALARY_FREQ.map(s=><option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-group"><label className="form-label">Bank Name</label><input className="form-input" value={form.bank_name} onChange={e=>setForm(f=>({...f,bank_name:e.target.value}))} placeholder="e.g. Stanbic Bank" /></div>
            <div className="form-group"><label className="form-label">Bank Account Number</label><input className="form-input" value={form.bank_account} onChange={e=>setForm(f=>({...f,bank_account:e.target.value}))} /></div>
            <div className="form-group"><label className="form-label">Mobile Money Number</label><input className="form-input" value={form.mobile_money} onChange={e=>setForm(f=>({...f,mobile_money:e.target.value}))} placeholder="+256..." /></div>
          </div>

          {/* Documents */}
          <div style={{ fontWeight:700, fontSize:'0.85rem', color:'var(--green-deep)', marginBottom:10 }}>📎 Documents</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginBottom:16 }}>
            {[
              { field:'cv_url', label:'CV / Resume', icon:'📄' },
              { field:'contract_url', label:'Employment Contract', icon:'📝' },
              { field:'id_scan_url', label:'National ID Scan', icon:'🪪' },
            ].map(({ field, label, icon }) => (
              <div key={field} style={{ background:'var(--cream)', borderRadius:8, padding:12 }}>
                <div style={{ fontWeight:700, fontSize:'0.78rem', marginBottom:8 }}>{icon} {label}</div>
                {form[field] ? (
                  <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                    <a href={form[field]} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm">👁 View</a>
                    <button className="btn btn-ghost btn-sm" onClick={()=>setForm(f=>({...f,[field]:''}))} style={{ color:'var(--red)' }}>✕ Remove</button>
                  </div>
                ) : (
                  <div style={{ display:'flex', gap:6 }}>
                    <button className="btn btn-secondary btn-sm" disabled={uploadingDoc} onClick={()=>{
                      const el = document.createElement('input'); el.type='file'; el.accept='image/*'; el.capture='environment'
                      el.onchange = e => handleDocUpload(field, e.target.files[0]); el.click()
                    }}>📷 Camera</button>
                    <button className="btn btn-secondary btn-sm" disabled={uploadingDoc} onClick={()=>{
                      const el = document.createElement('input'); el.type='file'; el.accept='image/*,application/pdf,.doc,.docx'
                      el.onchange = e => handleDocUpload(field, e.target.files[0]); el.click()
                    }}>📁 Upload</button>
                    {uploadingDoc && <span className="spinner dark" style={{ width:16, height:16 }}/>}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Notes */}
          <div className="form-group" style={{ marginBottom:16 }}>
            <label className="form-label">Notes / Additional Info</label>
            <textarea className="form-textarea" style={{ minHeight:80 }} value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} placeholder="Skills, qualifications, performance notes, disciplinary records, etc." />
          </div>

          <div style={{ display:'flex', gap:10 }}>
            <button className="btn btn-primary btn-lg" onClick={handleSave} disabled={saving}>{saving?<><span className="spinner"/>Saving...</>:'💾 Save Staff Record'}</button>
            <button className="btn btn-ghost" onClick={()=>{setTab('list');setShowForm(false)}}>Cancel</button>
          </div>
        </div>
      )}
    </AppLayout>
  )
}
